import { scheduledDark, type MonitorTheme, type MonitorTile } from '@serviceportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Maximize, Moon, Sun, SunMoon, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MonitorGrid, type TileData } from '@/components/monitor-tiles';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface PublicMonitor {
  name: string;
  tenant: string;
  columns: number;
  refreshSeconds: number;
  theme: MonitorTheme;
  darkFrom: string;
  darkUntil: string;
  tiles: (MonitorTile & { data: TileData })[];
  generatedAt: string;
}

/** Statusmonitor ohne Anmeldung (Wandanzeige in Werkstatt oder Büro). */
export function MonitorPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [refresh, setRefresh] = useState(60);
  const q = useQuery({
    queryKey: ['public-monitor', token],
    queryFn: () => api<PublicMonitor>(`/public/monitors/${encodeURIComponent(token)}`),
    refetchInterval: refresh * 1000,
    refetchIntervalInBackground: true,
    retry: (n, e) => !(e instanceof ApiError && (e.status === 404 || e.status === 403)) && n < 3,
  });
  const m = q.data;

  useEffect(() => {
    if (m) setRefresh(m.refreshSeconds);
  }, [m]);

  // Darstellung: Vorgabe des Monitors, am Gerät übersteuerbar (gemerkt je Bildschirm)
  const overrideKey = `sp-monitor-theme-${token}`;
  const [override, setOverride] = useState<'light' | 'dark' | null>(() => {
    try {
      const v = localStorage.getItem(overrideKey);
      return v === 'light' || v === 'dark' ? v : null;
    } catch {
      return null;
    }
  });
  const changeOverride = (v: 'light' | 'dark' | null) => {
    setOverride(v);
    try {
      if (v) localStorage.setItem(overrideKey, v);
      else localStorage.removeItem(overrideKey);
    } catch {
      /* nur Komfort */
    }
  };
  const [now, setNow] = useState(() => new Date());
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(media.matches);
    media.addEventListener('change', onChange);
    return () => {
      clearInterval(t);
      media.removeEventListener('change', onChange);
    };
  }, []);
  const dark = !m
    ? null
    : override
      ? override === 'dark'
      : m.theme === 'auto'
        ? systemDark
        : m.theme === 'schedule'
          ? scheduledDark(now, m.darkFrom, m.darkUntil)
          : m.theme === 'dark';

  // Farbschema des Monitors; beim Verlassen wieder das der Anwendung
  const title = m ? `${m.name} – ${m.tenant}` : null;
  useEffect(() => {
    if (dark === null || !title) return;
    const root = document.documentElement;
    const before = root.classList.contains('dark');
    root.classList.toggle('dark', dark);
    document.title = title;
    return () => {
      root.classList.toggle('dark', before);
    };
  }, [dark, title]);

  // Bildschirm wach halten (wenn der Browser es unterstützt)
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const request = async () => {
      try {
        const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
        if (document.visibilityState === 'visible' && nav.wakeLock) lock = await nav.wakeLock.request('screen');
      } catch {
        /* nicht erlaubt oder nicht unterstützt */
      }
    };
    void request();
    document.addEventListener('visibilitychange', request);
    return () => {
      document.removeEventListener('visibilitychange', request);
      void lock?.release().catch(() => {});
    };
  }, []);

  if (q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 403)) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold">Statusmonitor nicht verfügbar</h1>
          <p className="mt-2 text-muted-foreground">{q.error.status === 403 ? q.error.message : 'Die Adresse ist ungültig, wurde erneuert oder der Monitor ist ausgeschaltet.'}</p>
        </div>
      </div>
    );
  }
  if (!m) return <div className="min-h-dvh bg-background" />;

  const stale = q.isError || Date.now() - q.dataUpdatedAt > m.refreshSeconds * 3000;
  const data = Object.fromEntries(m.tiles.map((t) => [t.id, t.data]));
  return (
    <div className="flex min-h-dvh flex-col gap-[clamp(0.5rem,1vw,1.25rem)] bg-background p-[clamp(0.5rem,1vw,1.25rem)] text-foreground md:h-dvh">
      <header className="flex items-center justify-between gap-4">
        <h1 className="truncate text-[clamp(1rem,1.6vw,2rem)] font-semibold">
          {m.name} <span className="font-normal text-muted-foreground">· {m.tenant}</span>
        </h1>
        <div className="flex items-center gap-3 text-[clamp(0.7rem,0.9vw,1rem)] text-muted-foreground">
          {stale && (
            <span className="inline-flex items-center gap-1 text-destructive">
              <WifiOff className="size-4" /> Verbindung unterbrochen
            </span>
          )}
          <span className={cn(stale && 'text-destructive')}>Stand {new Date(q.dataUpdatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded p-1 hover:bg-accent"
            aria-label="Darstellung umschalten"
            title={override ? `An diesem Gerät ${override === 'dark' ? 'dunkel' : 'hell'} – klicken zum Wechseln` : 'Darstellung wie eingestellt – klicken für hell, dunkel'}
            onClick={() => changeOverride(override === null ? 'light' : override === 'light' ? 'dark' : null)}
          >
            {override === null ? <SunMoon className="size-4" /> : override === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
          </button>
          {document.fullscreenEnabled && (
            <button
              type="button"
              className="rounded p-1 hover:bg-accent"
              aria-label="Vollbild"
              onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {})}
            >
              <Maximize className="size-4" />
            </button>
          )}
        </div>
      </header>
      <MonitorGrid columns={m.columns} tiles={m.tiles} data={data} className="min-h-0 flex-1" />
    </div>
  );
}
